/**
 * Visual residue derived from the ledger. Does not mutate brick states,
 * crack paths, or integrity — stains / film / scorch / crack-visibility
 * only. All noise is seeded; no time terms.
 */
import { hash01 } from './hash';
import type { DamageLedger } from './ledger';
import { clamp01 } from './palette';
import { BRICK_PROFILE } from './profiles';
import type { DamageMaterialProfile, WeaponKind } from './types';
import { jointMid } from './wallGrid';

export interface ResidueStamp {
  x: number;
  y: number;
  energy: number;
  blastFraction: number;
  weapon: WeaponKind;
  index: number;
}

export interface StainRecord {
  jointId: number;
  x: number;
  y0: number;
  y1: number;
  width: number;
  seed: number;
}

export interface ResidueState {
  readonly film: Float32Array;
  readonly scorch: Float32Array;
  readonly crackVis: Float32Array;
  readonly stains: readonly StainRecord[];
  readonly stainCount: number;
  readonly scorchCount: number;
  readonly filmCount: number;
}

export const CRACK_HALO_PITCHES = 2.85;
export const CRACK_CORE_PITCHES = 0.55;
export const FILM_RADIUS_K = 0.95;
export const SCORCH_RADIUS_K = 1.05;
export const RESIDUE_MAP_W = 512;
export const RESIDUE_MAP_H = 320;

function smoothstep(e0: number, e1: number, x: number): number {
  const denom = e1 - e0;
  const t = Math.min(1, Math.max(0, (x - e0) / (denom === 0 ? 1e-8 : denom)));
  return t * t * (3 - 2 * t);
}

function valueNoise(x: number, y: number, seed: number): number {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const fx = x - ix;
  const fy = y - iy;
  const ux = fx * fx * (3 - 2 * fx);
  const uy = fy * fy * (3 - 2 * fy);
  const n00 = hash01(ix, iy, seed);
  const n10 = hash01(ix + 1, iy, seed);
  const n01 = hash01(ix, iy + 1, seed);
  const n11 = hash01(ix + 1, iy + 1, seed);
  return n00 * (1 - ux) * (1 - uy) + n10 * ux * (1 - uy) + n01 * (1 - ux) * uy + n11 * ux * uy;
}

export function emptyResidue(count: number): ResidueState {
  return {
    film: new Float32Array(count),
    scorch: new Float32Array(count),
    crackVis: new Float32Array(count),
    stains: [],
    stainCount: 0,
    scorchCount: 0,
    filmCount: 0,
  };
}

function distToRemoved(ledger: DamageLedger, x: number, y: number, removed: number[]): number {
  let best = Infinity;
  for (let k = 0; k < removed.length; k += 1) {
    const p = ledger.grid.bricks[removed[k]];
    const d = Math.hypot(p.x - x, p.y - y);
    if (d < best) best = d;
  }
  return best;
}

export function buildResidue(
  ledger: DamageLedger,
  stamps: readonly ResidueStamp[],
  profile: DamageMaterialProfile = BRICK_PROFILE,
): ResidueState {
  const n = ledger.bricks.length;
  const film = new Float32Array(n);
  const scorch = new Float32Array(n);
  const crackVis = new Float32Array(n);
  const pitch = ledger.grid.spec.pitchX;
  const halo = pitch * profile.crackVis.haloPitches;
  const core = pitch * profile.crackVis.corePitches;

  const removed: number[] = [];
  for (let i = 0; i < n; i += 1) {
    if (ledger.bricks[i].state === 'removed') removed.push(i);
  }

  for (let i = 0; i < n; i += 1) {
    const place = ledger.grid.bricks[i];
    const brick = ledger.bricks[i];
    if (brick.state === 'removed') {
      crackVis[i] = 0;
      continue;
    }
    const dHole = removed.length ? distToRemoved(ledger, place.x, place.y, removed) : Infinity;
    crackVis[i] = Number.isFinite(dHole) ? smoothstep(halo, core, dHole) : 0;

    let filmAmt = 0;
    let scorchAmt = 0;
    for (let s = 0; s < stamps.length; s += 1) {
      const st = stamps[s];
      const dx = place.x - st.x;
      const dy = place.y - st.y;
      const d = Math.hypot(dx, dy);
      const e = Math.max(0.2, st.energy);
      const rFilm = (0.42 + e * 0.38) * profile.film.radiusK;
      const below = place.y < st.y - 0.02 ? 1 : 0.38;
      const patch = 0.55 + 0.45 * hash01(brick.seed, st.index, 61);
      filmAmt = Math.max(filmAmt, smoothstep(rFilm, rFilm * 0.12, d) * below * 0.72 * patch);
      if (st.blastFraction > 0.5) {
        const rS = (0.55 + e * 0.5) * profile.scorch.radiusK;
        const nse = valueNoise(place.x * 2.4, place.y * 2.4, st.index * 17 + 9);
        const edge = rS * (0.7 + nse * 0.32);
        scorchAmt = Math.max(scorchAmt, smoothstep(edge, edge * 0.18, d));
      }
    }
    film[i] = clamp01(filmAmt);
    scorch[i] = clamp01(scorchAmt);
  }

  const stains: StainRecord[] = [];
  for (let j = 0; j < ledger.joints.length; j += 1) {
    const rec = ledger.joints[j];
    if (!rec.cracked) continue;
    const def = ledger.grid.joints[j];
    const vis = Math.max(crackVis[def.a] ?? 0, crackVis[def.b] ?? 0);
    if (vis < profile.stain.visMin) continue;
    const mid = jointMid(def);
    const seed = hash01(def.id, 83, rec.crackDepth);
    const len = (profile.stain.minLen + seed * profile.stain.lenRange) * (0.55 + vis * 0.45);
    const width = profile.stain.minWidth + hash01(def.id, 91) * profile.stain.widthRange;
    const yTop = Math.min(def.y0, def.y1);
    const y0 = def.kind === 'bed' ? mid.y : yTop;
    const xJitter = (hash01(def.id, 101) - 0.5) * Math.max(0.004, Math.abs(def.x1 - def.x0) * 0.15);
    stains.push({
      jointId: def.id,
      x: (def.kind === 'head' ? def.x0 : mid.x) + xJitter,
      y0,
      y1: y0 - len,
      width,
      seed,
    });
  }

  let scorchCount = 0;
  let filmCount = 0;
  for (let i = 0; i < n; i += 1) {
    if (scorch[i] > 0.08) scorchCount += 1;
    if (film[i] > 0.05) filmCount += 1;
  }

  return {
    film,
    scorch,
    crackVis,
    stains,
    stainCount: stains.length,
    scorchCount,
    filmCount,
  };
}

export function residueSnapshot(state: ResidueState): string {
  const f = Array.from(state.film, (v) => v.toFixed(3)).join(',');
  const s = Array.from(state.scorch, (v) => v.toFixed(3)).join(',');
  const c = Array.from(state.crackVis, (v) => v.toFixed(3)).join(',');
  const st = state.stains
    .map((t) => `${t.jointId}:${t.x.toFixed(3)}:${t.y0.toFixed(3)}:${t.y1.toFixed(3)}:${t.width.toFixed(3)}`)
    .join('|');
  return `${f}#${s}#${c}#${st}`;
}

export interface ResidueMapBounds {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

function distToSeg(px: number, py: number, x0: number, y0: number, x1: number, y1: number): number {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const l2 = dx * dx + dy * dy;
  if (l2 < 1e-10) return Math.hypot(px - x0, py - y0);
  let t = ((px - x0) * dx + (py - y0) * dy) / l2;
  t = Math.min(1, Math.max(0, t));
  return Math.hypot(px - (x0 + t * dx), py - (y0 + t * dy));
}

/**
 * Low-frequency RGBA map: R = dust film, G = bleed stain, B = scorch.
 * Band-limited (value-noise, not per-pixel hash) so orbit is shimmer-safe.
 */
export function bakeResidueMap(
  state: ResidueState,
  stamps: readonly ResidueStamp[],
  bounds: ResidueMapBounds,
  width = RESIDUE_MAP_W,
  height = RESIDUE_MAP_H,
): Uint8Array {
  const data = new Uint8Array(width * height * 4);
  const wSpan = Math.max(1e-4, bounds.x1 - bounds.x0);
  const hSpan = Math.max(1e-4, bounds.y1 - bounds.y0);

  const put = (ix: number, iy: number, r: number, g: number, b: number) => {
    if (ix < 0 || iy < 0 || ix >= width || iy >= height) return;
    const i = (iy * width + ix) * 4;
    data[i] = Math.max(data[i], Math.round(clamp01(r) * 255));
    data[i + 1] = Math.max(data[i + 1], Math.round(clamp01(g) * 255));
    data[i + 2] = Math.max(data[i + 2], Math.round(clamp01(b) * 255));
    data[i + 3] = 255;
  };

  const worldToPx = (x: number, y: number): { px: number; py: number } => ({
    px: ((x - bounds.x0) / wSpan) * (width - 1),
    py: (1 - (y - bounds.y0) / hSpan) * (height - 1),
  });

  for (let s = 0; s < stamps.length; s += 1) {
    const st = stamps[s];
    const e = Math.max(0.2, st.energy);
    const rFilm = (0.42 + e * 0.38) * FILM_RADIUS_K;
    const rScorch = st.blastFraction > 0.5 ? (0.55 + e * 0.5) * SCORCH_RADIUS_K : 0;
    const rMax = Math.max(rFilm, rScorch);
    const a = worldToPx(st.x - rMax, st.y - rMax * 1.35);
    const b = worldToPx(st.x + rMax, st.y + rMax);
    const x0 = Math.max(0, Math.floor(Math.min(a.px, b.px)));
    const x1 = Math.min(width - 1, Math.ceil(Math.max(a.px, b.px)));
    const y0 = Math.max(0, Math.floor(Math.min(a.py, b.py)));
    const y1 = Math.min(height - 1, Math.ceil(Math.max(a.py, b.py)));
    for (let iy = y0; iy <= y1; iy += 1) {
      for (let ix = x0; ix <= x1; ix += 1) {
        const wx = bounds.x0 + (ix / (width - 1)) * wSpan;
        const wy = bounds.y0 + (1 - iy / (height - 1)) * hSpan;
        const d = Math.hypot(wx - st.x, wy - st.y);
        const below = wy < st.y - 0.02 ? 1 : 0.4;
        const patch = 0.5 + 0.5 * valueNoise(wx * 3.2, wy * 3.2, st.index * 13 + 3);
        const filmAmt = smoothstep(rFilm, rFilm * 0.14, d) * below * 0.62 * patch;
        let scAmt = 0;
        if (rScorch > 0) {
          const nse = valueNoise(wx * 2.1, wy * 2.1, st.index * 29 + 7);
          const edge = rScorch * (0.68 + nse * 0.34);
          scAmt = smoothstep(edge, edge * 0.16, d);
        }
        put(ix, iy, filmAmt, 0, scAmt);
      }
    }
  }

  for (let t = 0; t < state.stains.length; t += 1) {
    const st = state.stains[t];
    const pad = st.width * 3.2;
    const a = worldToPx(st.x - pad, st.y1 - pad);
    const b = worldToPx(st.x + pad, st.y0 + pad);
    const x0 = Math.max(0, Math.floor(Math.min(a.px, b.px)));
    const x1 = Math.min(width - 1, Math.ceil(Math.max(a.px, b.px)));
    const y0 = Math.max(0, Math.floor(Math.min(a.py, b.py)));
    const y1 = Math.min(height - 1, Math.ceil(Math.max(a.py, b.py)));
    const wobble = (hash01(st.seed, 7) - 0.5) * 0.018;
    for (let iy = y0; iy <= y1; iy += 1) {
      for (let ix = x0; ix <= x1; ix += 1) {
        const wx = bounds.x0 + (ix / (width - 1)) * wSpan;
        const wy = bounds.y0 + (1 - iy / (height - 1)) * hSpan;
        const along = (st.y0 - wy) / Math.max(1e-4, st.y0 - st.y1);
        if (along < -0.05 || along > 1.08) continue;
        const xMid = st.x + wobble * along * along;
        const d = distToSeg(wx, wy, xMid, st.y0, xMid + wobble * 0.4, st.y1);
        const fade = (1 - smoothstep(0.55, 1.0, along)) * (0.75 + 0.25 * valueNoise(wx * 8, wy * 4, st.seed));
        const body = smoothstep(st.width * 1.8, st.width * 0.25, d) * fade;
        put(ix, iy, 0, body, 0);
      }
    }
  }

  for (let i = 3; i < data.length; i += 4) data[i] = 255;
  return data;
}
